import Ionicons from "@expo/vector-icons/Ionicons";
import { LinearGradient } from "expo-linear-gradient";
import * as Sharing from "expo-sharing";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ActivityIndicator,
  Image,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  useWindowDimensions,
  View,
} from "react-native";

import { Modal } from "@/components/ui/app-modal";

import { ThemedText } from "@/components/themed-text";
import { BlurredBackdrop } from "@/components/ui/blurred-backdrop";
import { Fonts, Radius, Spacing } from "@/constants/theme";
import { useTheme } from "@/hooks/use-theme";
import i18n from "@/i18n";
import { notify } from "@/lib/dialog";
import { shareText } from "@/lib/share-text";
import { fitFontSize } from "@/lib/poster-fit";
import { reflectionFeelingKey } from "@/lib/reflection-format";
import { storage } from "@/lib/storage";
import { useAuthStore } from "@/state/auth-store";
import type { ProgressDashboard, UserPlan } from "@/types";

import "./share-progress-card.css";

const EXPORT_WIDTH = 1080;
const EXPORT_HEIGHT = 1920;
const POSTER_NAVY = "#071F4D";

// Equal vertical spacing above and below the poster on mobile, added on top of
// the wrap's Spacing.md gap — so the Customize→poster gap matches the
// poster→Preview gap. On web we keep the natural spacing from the wrap gap.
const POSTER_GAP = Platform.OS === "web" ? 0 : 10;

function safeCount(n: number): string {
  return String(Math.max(0, n));
}

function shortDate(date: Date): string {
  return date.toLocaleDateString(i18n.language, {
    month: "short",
    day: "numeric",
  });
}

function dateStamp(summary: ProgressDashboard): string {
  const label =
    summary.rangeDays === 7
      ? i18n.t("shareProgress.thisWeek")
      : i18n.t("shareProgress.thisMonth");
  return `${label} · ${shortDate(new Date(summary.rangeStart))}-${shortDate(
    new Date(summary.rangeEnd),
  )}`;
}

/** Location-free lines summarising the week — safe to post anywhere. */
export function buildShareLines(summary: ProgressDashboard): string[] {
  const lines: string[] = [];
  // Pluralized like the four lines below them, which is what these three were
  // missing: they were raw template literals sitting directly above a correct
  // `t(..., { count })` call. The wording follows `progress-story`'s own
  // "{{count}} reflections logged" rather than inventing a second phrasing.
  if (summary.practice.completed > 0)
    lines.push(
      i18n.t("growth.shareProgress.practiceCompleted", {
        count: summary.practice.completed,
      }),
    );
  if (summary.goals.completed > 0)
    lines.push(
      i18n.t("growth.shareProgress.goalCompleted", {
        count: summary.goals.completed,
      }),
    );
  if (summary.reflections.total > 0)
    lines.push(
      i18n.t("growth.shareProgress.reflectionLogged", {
        count: summary.reflections.total,
      }),
    );
  if (summary.activity.conversations > 0)
    lines.push(
      i18n.t("shareProgress.conversationStarted", {
        count: summary.activity.conversations,
      }),
    );
  if (summary.activity.groups > 0)
    lines.push(
      i18n.t("shareProgress.groupJoined", {
        count: summary.activity.groups,
      }),
    );
  if (summary.activity.meetups > 0)
    lines.push(
      i18n.t("shareProgress.meetupMomentSparked", {
        count: summary.activity.meetups,
      }),
    );
  if (lines.length === 0) lines.push(i18n.t("shareProgress.showingUp"));
  return lines;
}

export function buildShareMessage(
  summary: ProgressDashboard,
): string {
  return [
    i18n.t("shareProgress.practicingWithOpncue"),
    "",
    dateStamp(summary),
    ...buildShareLines(summary).map((line) => `• ${line}`),
    "",
    i18n.t("shareProgress.smallStepsRealPeople"),
  ].join("\n");
}

export type ShareThemeKey =
  "classic" | "sky" | "mint" | "pink" | "ink" | "custom";

type ShareTheme = {
  key: ShareThemeKey;
  label: string;
  plus: boolean;
  accent: string;
  accentDark: string;
  halo: string;
  card: string;
  background: [string, string];
  text: string;
  muted: string;
};

type CustomColorOption = {
  key: string;
  label: string;
  accent: string;
  accentDark: string;
  halo: string;
  background: [string, string];
  text: string;
  muted: string;
};

type WebShareNavigator = Navigator & {
  canShare?: (data?: {
    files?: File[];
    title?: string;
    text?: string;
  }) => boolean;
  share?: (data?: {
    files?: File[];
    title?: string;
    text?: string;
  }) => Promise<void>;
};

export type ShareProgressStatKey =
  | "practices"
  | "activePractice"
  | "goalsAdded"
  | "goalsArchived"
  | "activeGoals"
  | "feelingEnergised"
  | "feelingComfortable"
  | "feelingCurious"
  | "feelingUnsure"
  | "feelingDisappointed"
  | "outcomeClicked"
  | "outcomeMoreComfortable"
  | "outcomeNervousStayed"
  | "outcomeLearnedAboutThem"
  | "outcomeLearnedAboutMyself"
  | "outcomeOneSided";

type ShareProgressStatOption = {
  key: ShareProgressStatKey;
  /**
   * A catalog KEY, never a string. This table is built at module scope, so a
   * resolved label here would freeze the language i18n held at import — which
   * is English on a cold start. Five of these already did exactly that, via
   * `reflectionFeelingKey(...)`; the rest were hardcoded English outright.
   */
  labelKey: string;
  labelFor?: (summary: ProgressDashboard) => string;
  group: ShareStatGroup;
  icon: keyof typeof Ionicons.glyphMap;
  value: (summary: ProgressDashboard) => number;
};

type StoredShareProgressPreferences = {
  version: 1;
  themeKey: ShareThemeKey;
  customColorKey: string;
  selectedStats: ShareProgressStatKey[];
};

const SHARE_PROGRESS_PREFERENCES_KEY = "opncue.shareProgressCard";

const DEFAULT_SHARE_STATS: ShareProgressStatKey[] = [
  "practices",
  "activeGoals",
  "feelingEnergised",
];

function breakdownCount(
  items: { key: string; count: number }[],
  key: string,
): number {
  return items.find((item) => item.key === key)?.count ?? 0;
}

const SHARE_STAT_OPTIONS: ShareProgressStatOption[] = [
  {
    key: "practices",
    labelKey: "growth.share.stats.practices",
    group: "practice",
    icon: "chatbubbles-outline",
    value: (summary) => summary.practice.completed,
  },
  {
    key: "activePractice",
    labelKey: "growth.share.stats.activePractice",
    group: "practice",
    icon: "flame-outline",
    value: (summary) => summary.practice.activePractice,
  },
  {
    // Key kept as-is: it is persisted in the user's saved share preferences.
    key: "goalsAdded",
    labelKey: "growth.share.stats.goalsAdded",
    group: "goals",
    icon: "add-circle-outline",
    value: (summary) => summary.goals.started,
  },
  {
    key: "activeGoals",
    labelKey: "growth.share.stats.activeGoals",
    group: "goals",
    icon: "flag-outline",
    value: (summary) => summary.goals.activeNow,
  },
  {
    key: "goalsArchived",
    labelKey: "growth.share.stats.goalsArchived",
    group: "goals",
    icon: "archive-outline",
    value: (summary) => summary.goals.archived,
  },
  {
    key: "feelingEnergised",
    labelKey:
      reflectionFeelingKey("energised") ?? "growth.options.feeling.energised",
    group: "reflections",
    icon: "flash-outline",
    value: (summary) =>
      breakdownCount(summary.reflections.byFeeling, "energised"),
  },
  {
    key: "feelingComfortable",
    labelKey:
      reflectionFeelingKey("comfortable") ?? "growth.options.feeling.comfortable",
    group: "reflections",
    icon: "happy-outline",
    value: (summary) =>
      breakdownCount(summary.reflections.byFeeling, "comfortable"),
  },
  {
    key: "feelingCurious",
    labelKey:
      reflectionFeelingKey("curious") ?? "growth.options.feeling.curious",
    group: "reflections",
    icon: "bulb-outline",
    value: (summary) =>
      breakdownCount(summary.reflections.byFeeling, "curious"),
  },
  {
    key: "feelingUnsure",
    labelKey:
      reflectionFeelingKey("unsure") ?? "growth.options.feeling.unsure",
    group: "reflections",
    icon: "help-circle-outline",
    value: (summary) =>
      breakdownCount(summary.reflections.byFeeling, "unsure"),
  },
  {
    key: "feelingDisappointed",
    labelKey:
      reflectionFeelingKey("disappointed") ?? "growth.options.feeling.disappointed",
    group: "reflections",
    icon: "rainy-outline",
    value: (summary) =>
      breakdownCount(summary.reflections.byFeeling, "disappointed"),
  },
  {
    key: "outcomeClicked",
    labelKey: "growth.share.outcome.clicked",
    group: "reflections",
    icon: "link-outline",
    value: (summary) =>
      breakdownCount(summary.reflections.byOutcome, "clicked"),
  },
  {
    key: "outcomeMoreComfortable",
    labelKey: "growth.share.outcome.moreComfortable",
    group: "reflections",
    icon: "sparkles-outline",
    value: (summary) =>
      breakdownCount(
        summary.reflections.byOutcome,
        "more_comfortable_than_expected",
      ),
  },
  {
    key: "outcomeNervousStayed",
    labelKey: "growth.share.outcome.nervousStayed",
    group: "reflections",
    icon: "heart-outline",
    value: (summary) =>
      breakdownCount(summary.reflections.byOutcome, "nervous_but_stayed"),
  },
  {
    key: "outcomeLearnedAboutThem",
    labelKey: "growth.share.outcome.learnedAboutThem",
    group: "reflections",
    icon: "person-outline",
    value: (summary) =>
      breakdownCount(summary.reflections.byOutcome, "learned_about_them"),
  },
  {
    key: "outcomeLearnedAboutMyself",
    labelKey: "growth.share.outcome.learnedAboutMyself",
    group: "reflections",
    icon: "person-circle-outline",
    value: (summary) =>
      breakdownCount(summary.reflections.byOutcome, "learned_about_myself"),
  },
  {
    key: "outcomeOneSided",
    labelKey: "growth.share.outcome.oneSided",
    group: "reflections",
    icon: "swap-horizontal-outline",
    value: (summary) =>
      breakdownCount(summary.reflections.byOutcome, "one_sided"),
  },
];
/**
 * Enum values, not labels. These used to be "Practice" / "Goals" /
 * "Reflections" — which the panel then rendered with `group.toUpperCase()`,
 * so one string was doing double duty as a discriminant and as a heading.
 * Translating it would have broken `option.group === group`; not translating
 * it left three English headings above translated labels.
 */
const SHARE_STAT_GROUPS = ["practice", "goals", "reflections"] as const;
type ShareStatGroup = (typeof SHARE_STAT_GROUPS)[number];

const STAT_GROUP_KEYS: Record<ShareStatGroup, string> = {
  practice: "growth.share.statGroups.practice",
  goals: "growth.share.statGroups.goals",
  reflections: "growth.share.statGroups.reflections",
};

const STAT_GROUP_HEADLINE_KEYS: Record<ShareStatGroup, string> = {
  practice: "growth.share.headline.practice",
  goals: "growth.share.headline.goals",
  reflections: "growth.share.headline.reflections",
};

function statLabel(
  option: ShareProgressStatOption,
  summary: ProgressDashboard,
): string {
  return option.labelFor?.(summary) ?? i18n.t(option.labelKey);
}

/**
 * Fill a selection up to three stats — the card ALWAYS carries three.
 *
 * Fewer left it looking abandoned: one lonely number floating in white space,
 * for exactly the people the subtitle promises "progress does not have to
 * look perfect" to. Metrics the user actually has data for go first; the
 * defaults cover the rest, and a zero renders as its own kind of honesty.
 */
function padShareStats(
  selected: ShareProgressStatKey[],
  summary?: ProgressDashboard,
): ShareProgressStatKey[] {
  const padded = selected.slice(0, 3);
  const add = (key: ShareProgressStatKey) => {
    if (padded.length < 3 && !padded.includes(key)) padded.push(key);
  };
  if (summary) {
    for (const option of SHARE_STAT_OPTIONS) {
      if (option.value(summary) > 0) add(option.key);
    }
  }
  for (const key of DEFAULT_SHARE_STATS) add(key);
  return padded;
}

function suggestedShareStats(
  summary: ProgressDashboard,
): ShareProgressStatKey[] {
  const selected: ShareProgressStatKey[] = [];
  // One stat per group first, so the suggestion spans the user's week rather
  // than mining a single feature three times.
  for (const group of SHARE_STAT_GROUPS) {
    const option = SHARE_STAT_OPTIONS.find(
      (item) => item.group === group && item.value(summary) > 0,
    );
    if (option) selected.push(option.key);
    if (selected.length === 3) return selected;
  }
  return padShareStats(selected, summary);
}

function selectedShareStats(
  selectedStats: ShareProgressStatKey[],
  summary: ProgressDashboard,
) {
  // Padded here as well as at the sources, so the rendered card holds the
  // three-stat floor even against a stale or hand-edited stored selection.
  const keys = padShareStats(
    selectedStats.filter((key) =>
      SHARE_STAT_OPTIONS.some((option) => option.key === key),
    ),
    summary,
  );
  const safeSelected = keys
    .map((key) => SHARE_STAT_OPTIONS.find((option) => option.key === key))
    .filter((option): option is ShareProgressStatOption => Boolean(option));

  return safeSelected.map((option) => ({
    ...option,
    label: statLabel(option, summary),
    count: option.value(summary),
  }));
}

/**
 * The line printed across the poster — the most public copy in the product.
 *
 * It was five English sentences in a function, which is why no scan over JSX
 * ever saw it. They are also modelled first-person speech, so French carries a
 * register decision here and not just a translation.
 */
function shareHeadline(key: ShareProgressStatKey, count: number): string {
  if (count === 0) return i18n.t("growth.share.headline.none");
  const group = SHARE_STAT_OPTIONS.find((option) => option.key === key)?.group;
  // An explicit map, not a template literal: a key built by interpolation is
  // invisible to `validate:i18n`, which is how untranslated copy hides.
  if (group) return i18n.t(STAT_GROUP_HEADLINE_KEYS[group]);
  return i18n.t("growth.share.headline.fallback");
}

function cssToken(value: string): string {
  return value.replace(/[^a-zA-Z0-9_-]/g, "-");
}

function themeClassName(theme: ShareTheme): string {
  if (theme.key !== "custom") return `opncue-progress-poster--${theme.key}`;
  const option =
    CUSTOM_COLOR_OPTIONS.find((item) => item.accent === theme.accent) ??
    CUSTOM_COLOR_OPTIONS[0];
  return `opncue-progress-poster--custom-${cssToken(option.key)}`;
}

function mainValueFitClass(text: string): string {
  if (text.length <= 3) return "opncue-progress-poster__main-value--fit-1";
  if (text.length === 4) return "opncue-progress-poster__main-value--fit-4";
  if (text.length === 5) return "opncue-progress-poster__main-value--fit-5";
  return "opncue-progress-poster__main-value--fit-long";
}

function posterLabelFitClass(text: string): string {
  const scale = text.length <= 22 ? 1 : Math.max(0.5, 22 / text.length);
  if (scale >= 0.92) return "opncue-progress-poster__label--fit-1";
  if (scale >= 0.78) return "opncue-progress-poster__label--fit-2";
  if (scale >= 0.64) return "opncue-progress-poster__label--fit-3";
  return "opncue-progress-poster__label--fit-4";
}

function secondaryValueFitClass(text: string): string {
  if (text.length <= 4) return "opncue-progress-poster__secondary-value--fit-1";
  if (text.length <= 6) return "opncue-progress-poster__secondary-value--fit-2";
  return "opncue-progress-poster__secondary-value--fit-long";
}

function secondaryLabelFitClass(text: string): string {
  const scale = text.length <= 16 ? 1 : Math.max(0.5, 16 / text.length);
  if (scale >= 0.92) return "opncue-progress-poster__secondary-label--fit-1";
  if (scale >= 0.78) return "opncue-progress-poster__secondary-label--fit-2";
  if (scale >= 0.64) return "opncue-progress-poster__secondary-label--fit-3";
  return "opncue-progress-poster__secondary-label--fit-4";
}

function ioniconGlyph(name: keyof typeof Ionicons.glyphMap): string {
  const glyph = Ionicons.glyphMap[name];
  return typeof glyph === "number" ? String.fromCodePoint(glyph) : glyph;
}

const SHARE_THEMES: ShareTheme[] = [
  {
    key: "classic",
    get label() {
      return i18n.t("growth.share.styles.classic");
    },
    plus: false,
    accent: "#0B7DFF",
    accentDark: "#071F4D",
    halo: "#EAF4FF",
    card: "#FFFFFF",
    background: ["#F4FAFF", "#E1F0FF"],
    text: "#061A44",
    muted: "#6E7D98",
  },
  {
    key: "sky",
    get label() {
      return i18n.t("growth.share.styles.sky");
    },
    plus: true,
    accent: "#1D9BF0",
    accentDark: "#082F5F",
    halo: "#E7F6FF",
    card: "#FFFFFF",
    background: ["#F7FCFF", "#D8ECFF"],
    text: "#062B55",
    muted: "#667A96",
  },
  {
    key: "mint",
    get label() {
      return i18n.t("growth.share.styles.mint");
    },
    plus: true,
    accent: "#059B8E",
    accentDark: "#063F3B",
    halo: "#E7FAF6",
    card: "#FFFFFF",
    background: ["#F6FFFC", "#DDF6EF"],
    text: "#083E3A",
    muted: "#647A76",
  },
  {
    key: "pink",
    get label() {
      return i18n.t("growth.share.styles.pink");
    },
    plus: true,
    accent: "#EC4899",
    accentDark: "#831843",
    halo: "#FCE7F3",
    card: "#FFFFFF",
    background: ["#FFF7FB", "#FCE7F3"],
    text: "#3B1028",
    muted: "#8A6A78",
  },
  {
    key: "ink",
    get label() {
      return i18n.t("growth.share.styles.ink");
    },
    plus: true,
    accent: "#7DB7FF",
    accentDark: "#F7FBFF",
    halo: "#15243D",
    card: "#071529",
    background: ["#0D1B2E", "#132A46"],
    text: "#F7FBFF",
    muted: "#AAB8CD",
  },
];

const DISPLAYED_SHARE_THEME_KEYS: ShareThemeKey[] = ["classic", "pink", "mint"];

const CUSTOM_COLOR_OPTIONS: CustomColorOption[] = [
  {
    key: "amber",
    get label() {
      return i18n.t("growth.share.styles.amber");
    },
    accent: "#D97706",
    accentDark: "#63370A",
    halo: "#FEF3C7",
    background: ["#FFFDF5", "#FEF3C7"],
    text: "#3E2508",
    muted: "#806F55",
  },
  {
    key: "violet",
    get label() {
      return i18n.t("growth.share.styles.violet");
    },
    accent: "#8B5CF6",
    accentDark: "#3B176D",
    halo: "#EDE9FE",
    background: ["#FBFAFF", "#EDE9FE"],
    text: "#29124D",
    muted: "#74688E",
  },
  {
    key: "coral",
    get label() {
      return i18n.t("growth.share.styles.coral");
    },
    accent: "#F97316",
    accentDark: "#7C2D12",
    halo: "#FFEDD5",
    background: ["#FFF8F1", "#FFEDD5"],
    text: "#44200D",
    muted: "#846D5C",
  },
  {
    key: "ruby",
    get label() {
      return i18n.t("growth.share.styles.ruby");
    },
    accent: "#DC2626",
    accentDark: "#7F1D1D",
    halo: "#FEE2E2",
    background: ["#FFF7F7", "#FEE2E2"],
    text: "#3D1010",
    muted: "#856A6A",
  },
  {
    key: "indigo",
    get label() {
      return i18n.t("growth.share.styles.indigo");
    },
    accent: "#4F46E5",
    accentDark: "#312E81",
    halo: "#E0E7FF",
    background: ["#F8FAFF", "#E0E7FF"],
    text: "#1E1B4B",
    muted: "#6A6F8F",
  },
  {
    key: "lime",
    get label() {
      return i18n.t("growth.share.styles.lime");
    },
    accent: "#65A30D",
    accentDark: "#365314",
    halo: "#ECFCCB",
    background: ["#FBFFF4", "#ECFCCB"],
    text: "#25330D",
    muted: "#687755",
  },
  {
    key: "graphite",
    get label() {
      return i18n.t("growth.share.styles.graphite");
    },
    accent: "#64748B",
    accentDark: "#1E293B",
    halo: "#E2E8F0",
    background: ["#F8FAFC", "#E2E8F0"],
    text: "#172033",
    muted: "#687385",
  },
];

function shareProgressPreferencesKey(userId: string | undefined): string {
  return userId
    ? `${SHARE_PROGRESS_PREFERENCES_KEY}.${userId}`
    : `${SHARE_PROGRESS_PREFERENCES_KEY}.local`;
}

function isShareThemeKey(value: unknown): value is ShareThemeKey {
  return (
    value === "custom" || SHARE_THEMES.some((theme) => theme.key === value)
  );
}

function canUseShareTheme(key: ShareThemeKey, isPlus: boolean): boolean {
  if (key === "custom") return isPlus;
  const theme = SHARE_THEMES.find((item) => item.key === key);
  return Boolean(theme && (!theme.plus || isPlus));
}

function isShareProgressStatKey(value: unknown): value is ShareProgressStatKey {
  return SHARE_STAT_OPTIONS.some((option) => option.key === value);
}

function sanitizeSelectedStats(value: unknown): ShareProgressStatKey[] {
  if (!Array.isArray(value)) return DEFAULT_SHARE_STATS;

  const selected = value.reduce<ShareProgressStatKey[]>((acc, item) => {
    if (isShareProgressStatKey(item) && !acc.includes(item) && acc.length < 3) {
      acc.push(item);
    }
    return acc;
  }, []);

  // Stored preferences predating the three-stat floor may hold one or two
  // picks; the defaults top them up.
  return padShareStats(selected);
}

function parseShareProgressPreferences(
  raw: string | null,
  isPlus: boolean,
): StoredShareProgressPreferences | null {
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw) as Partial<StoredShareProgressPreferences>;
    const themeKey =
      isShareThemeKey(parsed.themeKey) &&
      canUseShareTheme(parsed.themeKey, isPlus)
        ? parsed.themeKey
        : "classic";
    const customColor =
      CUSTOM_COLOR_OPTIONS.find(
        (option) => option.key === parsed.customColorKey,
      ) ?? CUSTOM_COLOR_OPTIONS[0];

    return {
      version: 1,
      themeKey,
      customColorKey: customColor.key,
      selectedStats: sanitizeSelectedStats(parsed.selectedStats),
    };
  } catch {
    return null;
  }
}

function downloadDataUri(filename: string, dataUri: string) {
  if (Platform.OS !== "web" || typeof document === "undefined") return;
  const link = document.createElement("a");
  link.href = dataUri;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
}

async function shareWebImage(
  dataUri: string,
  filename: string,
): Promise<boolean> {
  if (Platform.OS !== "web" || typeof navigator === "undefined") return false;
  const nav = navigator as WebShareNavigator;
  if (!nav.share) return false;

  const response = await fetch(dataUri);
  const blob = await response.blob();
  const file = new File([blob], filename, { type: "image/png" });
  const payload = {
    files: [file],
    title: i18n.t("shareProgress.shareProgress"),
    text: i18n.t("shareProgress.practicingWithOpncue"),
  };

  if (nav.canShare && !nav.canShare(payload)) return false;
  await nav.share(payload);
  return true;
}

function PosterArtwork({
  theme,
  summary,
  selectedStats,
}: {
  theme: ShareTheme;
  summary: ProgressDashboard;
  selectedStats: ShareProgressStatKey[];
}) {
  const { t } = useTranslation();
  const stats = selectedShareStats(selectedStats, summary);
  const mainStat = stats[0];
  const secondaryStats = stats.slice(1);

  if (Platform.OS === "web") {
    const mainCount = safeCount(mainStat.count);
    return (
      <div
        className={`opncue-progress-poster ${themeClassName(theme)}`}
        aria-label={t("shareProgress.previewPosterA11y")}
      >
        <div className="opncue-progress-poster__shape opncue-progress-poster__shape--top" />
        <div className="opncue-progress-poster__shape opncue-progress-poster__shape--bottom" />

        <div className="opncue-progress-poster__card">
          <div className="opncue-progress-poster__header">
            <span className="opncue-progress-poster__heart" aria-hidden="true">
              {ioniconGlyph("heart-outline")}
            </span>
            <div className="opncue-progress-poster__kicker">
              {t("shareProgress.kicker")}
            </div>
            <div className="opncue-progress-poster__brand">
              {i18n.t("growth.share.brandSuffix")}
            </div>
            <div className="opncue-progress-poster__date">
              {dateStamp(summary)}
            </div>
          </div>

          <div className="opncue-progress-poster__headline">
            {shareHeadline(mainStat.key, mainStat.count)}
          </div>

          <div className="opncue-progress-poster__main">
            <div className="opncue-progress-poster__main-halo" />
            <div
              className={[
                "opncue-progress-poster__main-value",
                mainValueFitClass(mainCount),
              ].join(" ")}
            >
              {mainCount}
            </div>
          </div>

          <div
            className={[
              "opncue-progress-poster__label",
              posterLabelFitClass(mainStat.label),
            ].join(" ")}
          >
            {mainStat.label}
          </div>

          {secondaryStats.length > 0 ? (
            <div className="opncue-progress-poster__secondary-row">
              {secondaryStats.map((stat, index) => {
                const count = safeCount(stat.count);
                return (
                  <div
                    className="opncue-progress-poster__secondary-wrap"
                    key={stat.key}
                  >
                    {index > 0 ? (
                      <div className="opncue-progress-poster__secondary-divider" />
                    ) : null}
                    <div className="opncue-progress-poster__secondary-tile">
                      <span
                        className="opncue-progress-poster__stat-icon"
                        aria-hidden="true"
                      >
                        {ioniconGlyph(stat.icon)}
                      </span>
                      <div
                        className={[
                          "opncue-progress-poster__secondary-value",
                          secondaryValueFitClass(count),
                        ].join(" ")}
                      >
                        {count}
                      </div>
                      <div
                        className={[
                          "opncue-progress-poster__secondary-label",
                          secondaryLabelFitClass(stat.label),
                        ].join(" ")}
                      >
                        {stat.label}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="opncue-progress-poster__secondary-spacer" />
          )}

          <div className="opncue-progress-poster__footer">
            <div className="opncue-progress-poster__rule">
              <div className="opncue-progress-poster__rule-line" />
              <span className="opncue-progress-poster__sparkle" aria-hidden="true">
                {ioniconGlyph("sparkles")}
              </span>
              <div className="opncue-progress-poster__rule-line" />
            </div>

            <div className="opncue-progress-poster__tagline">
              <span>{i18n.t("growth.share.realSteps")}</span>{" "}
              <strong>{i18n.t("growth.share.realPeople")}</strong>
            </div>

            <div className="opncue-progress-poster__safe-stats">
              <span className="opncue-progress-poster__lock" aria-hidden="true">
                {ioniconGlyph("lock-closed-outline")}
              </span>
              <span>{t("shareProgress.safeStatsOnly")}</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <LinearGradient
      colors={theme.background}
      start={{ x: 0, y: 0 }}
      end={{ x: 1, y: 1 }}
      style={styles.posterBackground}
    >
      <View
        style={[
          styles.softShape,
          styles.softShapeTop,
          { backgroundColor: theme.halo },
        ]}
      />
      <View
        style={[
          styles.softShape,
          styles.softShapeBottom,
          { backgroundColor: theme.halo },
        ]}
      />

      <View
        style={[
          styles.poster,
          {
            backgroundColor: theme.card,
            shadowColor: "transparent",
            borderColor: theme.halo,
          },
        ]}
      >
        <View style={styles.posterHeader}>
          <Ionicons name="heart-outline" size={54} color={theme.accent} />
          <Text style={[styles.kicker, { color: theme.accent }]}>
            {t("shareProgress.kicker")}
          </Text>
          <Text style={[styles.brand, { color: theme.accent }]}>
            {i18n.t("growth.share.brandSuffix")}
          </Text>
          <Text style={[styles.dateStamp, { color: theme.muted }]}>
            {dateStamp(summary)}
          </Text>
        </View>

        <Text style={[styles.headline, { color: theme.text }]}>
          {shareHeadline(mainStat.key, mainStat.count)}
        </Text>

        <View style={styles.mainMetricWrap}>
          <View
            style={[styles.mainMetricHalo, { backgroundColor: theme.halo }]}
          />
          {/* Sized from the content, not by `adjustsFontSizeToFit` — which
              react-native-web does not implement, so this number never shrank
              on web and was clipped by `numberOfLines` instead. */}
          <Text
            style={[
              styles.mainMetric,
              {
                color: theme.accent,
                fontSize: fitFontSize(250, safeCount(mainStat.count), 3),
              },
            ]}
            numberOfLines={1}
          >
            {safeCount(mainStat.count)}
          </Text>
        </View>

        <Text
          style={[
            styles.mainMetricLabel,
            { color: theme.text, fontSize: fitFontSize(54, mainStat.label, 22) },
          ]}
          numberOfLines={2}
        >
          {mainStat.label}
        </Text>

        {secondaryStats.length > 0 ? (
          <View style={styles.secondaryRow}>
            {secondaryStats.map((stat, index) => (
              <View key={stat.key} style={styles.secondaryStatWrap}>
                {index > 0 ? (
                  <View
                    style={[
                      styles.secondaryDivider,
                      { backgroundColor: theme.accent },
                    ]}
                  />
                ) : null}
                <View
                  style={[
                    styles.secondaryTile,
                    { backgroundColor: theme.halo },
                  ]}
                >
                  <Ionicons name={stat.icon} size={60} color={theme.accent} />
                  <Text
                    style={[
                      styles.secondaryValue,
                      {
                        color: theme.accent,
                        fontSize: fitFontSize(78, safeCount(stat.count), 4),
                      },
                    ]}
                    numberOfLines={1}
                  >
                    {safeCount(stat.count)}
                  </Text>
                  <Text
                    style={[
                      styles.secondaryLabel,
                      { color: theme.text, fontSize: fitFontSize(34, stat.label, 16) },
                    ]}
                    numberOfLines={2}
                  >
                    {stat.label}
                  </Text>
                </View>
              </View>
            ))}
          </View>
        ) : (
          <View style={styles.secondaryRowSpacer} />
        )}

        <View style={styles.posterFooter}>
          <View style={styles.footerRule}>
            <View
              style={[styles.ruleLine, { backgroundColor: theme.accent }]}
            />
            <Ionicons name="sparkles" size={42} color={theme.accent} />
            <View
              style={[styles.ruleLine, { backgroundColor: theme.accent }]}
            />
          </View>

          <Text style={styles.footer}>
            <Text style={{ color: theme.accent }}>
              {i18n.t("growth.share.realSteps")}
            </Text>
            <Text> </Text>
            <Text style={{ color: POSTER_NAVY }}>
              {i18n.t("growth.share.realPeople")}
            </Text>
          </Text>

          <View style={styles.safeStats}>
            <Ionicons
              name="lock-closed-outline"
              size={25}
              color={theme.muted}
            />
            <Text style={[styles.safeStatsText, { color: theme.muted }]}>
              {t("shareProgress.safeStatsOnly")}
            </Text>
          </View>
        </View>
      </View>
    </LinearGradient>
  );
}

export function ShareProgressCard({
  summary,
  plan,
  onUpgrade,
}: {
  summary: ProgressDashboard;
  plan: UserPlan | undefined;
  onUpgrade?: () => void;
}) {
  const c = useTheme();
  const { t } = useTranslation();
  const { height: viewportHeight } = useWindowDimensions();
  const userId = useAuthStore((state) => state.user?.id);
  const exportRef = useRef<View>(null);
  const webExportRef = useRef<HTMLDivElement>(null);
  const isPlus = plan === "plus";
  const preferencesKey = shareProgressPreferencesKey(userId);
  const [themeKey, setThemeKey] = useState<ShareThemeKey>("classic");
  const [customColor, setCustomColor] = useState<CustomColorOption>(
    CUSTOM_COLOR_OPTIONS[0],
  );
  const [customPickerOpen, setCustomPickerOpen] = useState(false);
  const [previewUri, setPreviewUri] = useState<string | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [customizeOpen, setCustomizeOpen] = useState(false);
  const [expandedStatGroup, setExpandedStatGroup] =
    useState<ShareStatGroup | null>(null);
  const [selectedStats, setSelectedStats] = useState<ShareProgressStatKey[]>(
    () => suggestedShareStats(summary),
  );
  const [generatingPreview, setGeneratingPreview] = useState(true);
  const [sharing, setSharing] = useState(false);
  const [preferencesLoaded, setPreferencesLoaded] = useState(false);
  const [previewScrollMetrics, setPreviewScrollMetrics] = useState({
    contentHeight: 0,
    offsetY: 0,
    viewportHeight: 0,
  });
  const previewViewportHeight =
    Platform.OS === "web"
      ? Math.min(272, Math.round(viewportHeight * 0.4))
      : Math.min(240, Math.round(viewportHeight * 0.336));
  const previewCanScroll =
    previewScrollMetrics.contentHeight > previewScrollMetrics.viewportHeight + 2;
  const previewThumbHeight = previewCanScroll
    ? Math.max(
        34,
        (previewScrollMetrics.viewportHeight /
          previewScrollMetrics.contentHeight) *
          previewScrollMetrics.viewportHeight,
      )
    : 0;
  const previewThumbTop = previewCanScroll
    ? Math.min(
        previewScrollMetrics.viewportHeight - previewThumbHeight,
        (previewScrollMetrics.offsetY /
          Math.max(
            1,
            previewScrollMetrics.contentHeight -
              previewScrollMetrics.viewportHeight,
          )) *
          Math.max(0, previewScrollMetrics.viewportHeight - previewThumbHeight),
      )
    : 0;
  const customTheme: ShareTheme = {
    key: "custom",
    get label() {
      return i18n.t("growth.share.styles.custom");
    },
    plus: true,
    accent: customColor.accent,
    accentDark: customColor.accentDark,
    halo: customColor.halo,
    card: "#FFFFFF",
    background: customColor.background,
    text: customColor.text,
    muted: customColor.muted,
  };
  const displayedThemes = SHARE_THEMES.filter((item) =>
    DISPLAYED_SHARE_THEME_KEYS.includes(item.key),
  );
  const themeChoices = [...displayedThemes, customTheme];
  const theme =
    themeKey === "custom"
      ? customTheme
      : (SHARE_THEMES.find((item) => item.key === themeKey) ?? SHARE_THEMES[0]);

  useEffect(() => {
    let cancelled = false;

    async function loadPreferences() {
      setPreferencesLoaded(false);
      let stored: StoredShareProgressPreferences | null = null;
      try {
        stored = parseShareProgressPreferences(
          await storage.get(preferencesKey),
          isPlus,
        );
      } catch {
        stored = null;
      }
      if (cancelled) return;

      if (stored) {
        setThemeKey(stored.themeKey);
        setCustomColor(
          CUSTOM_COLOR_OPTIONS.find(
            (option) => option.key === stored.customColorKey,
          ) ?? CUSTOM_COLOR_OPTIONS[0],
        );
        setSelectedStats(stored.selectedStats);
        setCustomPickerOpen(false);
      } else {
        setThemeKey("classic");
        setCustomColor(CUSTOM_COLOR_OPTIONS[0]);
      }

      setPreferencesLoaded(true);
    }

    void loadPreferences();

    return () => {
      cancelled = true;
    };
  }, [preferencesKey, isPlus]);

  useEffect(() => {
    if (!preferencesLoaded) return;

    const preferences: StoredShareProgressPreferences = {
      version: 1,
      themeKey,
      customColorKey: customColor.key,
      selectedStats,
    };

    void storage.set(preferencesKey, JSON.stringify(preferences));
  }, [
    customColor.key,
    preferencesKey,
    preferencesLoaded,
    selectedStats,
    themeKey,
  ]);

  const capturePoster = useCallback(async () => {
    const target = Platform.OS === "web" ? webExportRef : exportRef;
    if (!target.current) throw new Error("Progress poster is not ready.");

    /**
     * Wait for the webfonts BEFORE capturing, or the poster is a race.
     *
     * html2canvas does not draw a text node in one call: it splits it with
     * `Intl.Segmenter`, measures each segment against the live DOM with
     * `Range.getBoundingClientRect()`, then redraws each at that position using
     * a canvas font rebuilt from computed styles. Measure and draw therefore
     * resolve the font twice. If a webfont lands between those two moments —
     * or is used for one and not the other — every word sits at a position
     * computed for different glyph widths, words creep together, and the
     * spaces between them vanish. That is not hypothetical: it is what
     * "ThisWeek· Aug25-Aug31" was.
     *
     * `document.fonts.ready` resolves once font loading has settled, so both
     * halves see the same face. Guarded rather than assumed: the API is
     * web-only and `capturePoster` also runs on native.
     */
    if (Platform.OS === "web" && typeof document !== "undefined" && document.fonts) {
      await document.fonts.ready;
    }

    // See vibe-card.tsx: module-scope on web means html2canvas on every route.
    const { captureRef } = await import("react-native-view-shot");
    return captureRef(target as Parameters<typeof captureRef>[0], {
      format: "png",
      quality: 1,
      result: Platform.OS === "web" ? "data-uri" : "tmpfile",
      width: EXPORT_WIDTH,
      height: EXPORT_HEIGHT,
    });
  }, []);

  const refreshPreview = useCallback(async () => {
    setGeneratingPreview(true);
    try {
      const uri = await capturePoster();
      setPreviewUri(uri);
    } catch {
      setPreviewUri(null);
    } finally {
      setGeneratingPreview(false);
    }
  }, [capturePoster]);

  useEffect(() => {
    const timer = setTimeout(() => {
      void refreshPreview();
    }, 80);

    return () => clearTimeout(timer);
  }, [
    refreshPreview,
    summary,
    selectedStats,
    customColor.key,
    themeKey,
  ]);

  async function shareTextFallback() {
    await shareText({
      message: buildShareMessage(summary),
    });
  }

  async function share() {
    if (sharing) return;
    setSharing(true);

    try {
      const uri = await capturePoster();
      setPreviewUri(uri);
      const filename = `opncue-progress-${new Date()
        .toISOString()
        .slice(0, 10)}.png`;

      if (Platform.OS === "web") {
        const shared = await shareWebImage(uri, filename);
        if (!shared) downloadDataUri(filename, uri);
        return;
      }

      if (await Sharing.isAvailableAsync()) {
        await Sharing.shareAsync(uri, {
          dialogTitle: t("shareProgress.shareProgress"),
          mimeType: "image/png",
          UTI: "public.png",
        });
        return;
      }

      await shareTextFallback();
    } catch {
      try {
        await shareTextFallback();
      } catch {
        // Close the preview before the notice: this runs with the preview
        // Modal open, and iOS will not present the dialog over it — the notice
        // never appears and its window eats every touch. Sharing has failed
        // twice by here, so the preview has nothing left to offer.
        setPreviewOpen(false);
        notify(
          t("shareProgress.shareFailedTitle"),
          t("shareProgress.shareFailedBody"),
        );
      }
    } finally {
      setSharing(false);
    }
  }

  function selectTheme(nextTheme: ShareTheme) {
    if (nextTheme.plus && !isPlus) {
      onUpgrade?.();
      return;
    }

    if (nextTheme.key === "custom") {
      setThemeKey("custom");
      setCustomPickerOpen((open) => (themeKey === "custom" ? !open : true));
      return;
    }

    setCustomPickerOpen(false);
    setThemeKey(nextTheme.key);
  }

  function selectCustomColor(option: CustomColorOption) {
    setCustomColor(option);
    setThemeKey("custom");
  }

  function openCustomization() {
    setExpandedStatGroup(null);
    setCustomizeOpen(true);
  }

  return (
    <View style={styles.wrap}>
      {Platform.OS === "web" ? (
        <div className="opncue-progress-export-host">
          <div ref={webExportRef} className="opncue-progress-export-frame">
            <PosterArtwork
              theme={theme}
              summary={summary}
              selectedStats={selectedStats}
            />
          </div>
        </div>
      ) : (
        <View style={styles.exportHost} pointerEvents="none">
          <View ref={exportRef} collapsable={false} style={styles.exportFrame}>
            <PosterArtwork
              theme={theme}
              summary={summary}
              selectedStats={selectedStats}
            />
          </View>
        </View>
      )}

      <Pressable
        accessibilityRole="button"
        accessibilityLabel={i18n.t("growth.share.customizeA11y")}
        onPress={openCustomization}
        style={({ pressed }) => [
          styles.customizeButton,
          { borderColor: c.border, backgroundColor: c.surfaceAlt },
          pressed && styles.pressed,
        ]}
      >
        <Ionicons name="options-outline" size={17} color={c.tint} />
        <ThemedText variant="bodyStrong" tone="tint">
          {i18n.t("growth.share.customize")}
        </ThemedText>
      </Pressable>

      <View style={styles.previewScrollFrame}>
        <ScrollView
          nestedScrollEnabled
          persistentScrollbar
          showsVerticalScrollIndicator={false}
          onContentSizeChange={(_, contentHeight) =>
            setPreviewScrollMetrics((current) => ({
              ...current,
              contentHeight,
            }))
          }
          onLayout={(event) => {
            const nextViewportHeight = event.nativeEvent.layout.height;
            setPreviewScrollMetrics((current) => ({
              ...current,
              viewportHeight: nextViewportHeight,
            }));
          }}
          onScroll={(event) => {
            const nextOffsetY = event.nativeEvent.contentOffset.y;
            setPreviewScrollMetrics((current) => ({
              ...current,
              offsetY: nextOffsetY,
            }));
          }}
          scrollEventThrottle={16}
          style={[
            styles.previewScroll,
            { maxHeight: Math.max(152, previewViewportHeight) },
          ]}
          contentContainerStyle={styles.previewScrollContent}
        >
          <Pressable
            accessibilityRole="button"
            accessibilityLabel={t("shareProgress.previewPosterA11y")}
            onPress={() => {
              if (previewUri) setPreviewOpen(true);
            }}
            style={({ pressed }) => [
              styles.previewButton,
              pressed && styles.pressed,
            ]}
          >
            {previewUri ? (
              <Image
                source={{ uri: previewUri }}
                resizeMode={Platform.OS === "web" ? "contain" : "cover"}
                style={styles.posterPreview}
              />
            ) : (
              <View
                style={[
                  styles.posterPreview,
                  styles.previewLoading,
                  { backgroundColor: c.surfaceAlt, borderColor: c.border },
                ]}
              >
                <ActivityIndicator color={c.tint} />
                <ThemedText variant="caption" tone="secondary" center>
                  {t("shareProgress.preparingPreview")}
                </ThemedText>
              </View>
            )}
          </Pressable>
        </ScrollView>

        {previewCanScroll ? (
          <View
            pointerEvents="none"
            style={[
              styles.previewScrollTrack,
              { backgroundColor: c.surfaceAlt, borderColor: c.border },
            ]}
          >
            <View
              style={[
                styles.previewScrollThumb,
                {
                  backgroundColor: c.tint,
                  height: previewThumbHeight,
                  transform: [{ translateY: previewThumbTop }],
                },
              ]}
            />
          </View>
        ) : null}
      </View>

      <View style={styles.previewActionRow}>
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={t("shareProgress.openPreviewA11y")}
          onPress={() => {
            if (previewUri) setPreviewOpen(true);
          }}
          disabled={!previewUri || generatingPreview}
          style={({ pressed }) => [
            styles.previewAction,
            { borderColor: c.border, backgroundColor: c.surfaceAlt },
            (pressed || generatingPreview) && styles.pressed,
          ]}
        >
          <Ionicons name="expand-outline" size={17} color={c.tint} />
          <ThemedText variant="bodyStrong" tone="tint">
            {t("common.preview")}
          </ThemedText>
        </Pressable>

        <Pressable
          accessibilityRole="button"
          accessibilityLabel={t("shareProgress.shareProgress")}
          onPress={share}
          disabled={!previewUri || generatingPreview || sharing}
          style={({ pressed }) => [
            styles.previewShareAction,
            { borderColor: c.border, backgroundColor: c.surfaceAlt },
            (pressed || generatingPreview || sharing) && styles.pressed,
          ]}
        >
          {sharing ? (
            <ActivityIndicator color={c.tint} />
          ) : (
            <Ionicons name="share-social-outline" size={18} color={c.tint} />
          )}
        </Pressable>
      </View>

      <View style={styles.themeRow}>
        {themeChoices.map((item) => {
          const active = item.key === themeKey;
          const locked = item.plus && !isPlus;
          const isCustom = item.key === "custom";
          return (
            <Pressable
              key={item.key}
              accessibilityRole="button"
              accessibilityLabel={`${t("shareProgress.posterStyleLabel", {
                styleName: item.label,
              })}${locked ? t("growth.share.plusSuffix") : ""}`}
              onPress={() => selectTheme(item)}
              style={[
                styles.swatch,
                {
                  backgroundColor: isCustom ? c.surfaceAlt : item.card,
                  borderColor: active ? c.tint : c.border,
                  borderWidth: active ? 2 : 1,
                },
              ]}
            >
              {isCustom ? (
                <Ionicons
                  name="add"
                  size={18}
                  color={locked ? c.textMuted : c.tint}
                />
              ) : (
                <View
                  style={[styles.swatchDot, { backgroundColor: item.accent }]}
                />
              )}
              {locked && !isCustom ? (
                <Ionicons
                  name="lock-closed"
                  size={11}
                  color={item.accentDark}
                  style={styles.swatchIcon}
                />
              ) : active && !isCustom ? (
                <Ionicons
                  name="checkmark"
                  size={12}
                  color={item.accentDark}
                  style={styles.swatchIcon}
                />
              ) : null}
            </Pressable>
          );
        })}
      </View>

      {customPickerOpen && isPlus ? (
        <View style={styles.customColorPanel}>
          <ThemedText variant="caption" tone="secondary" center>
            {i18n.t("growth.share.pickColor")}
          </ThemedText>
          <View style={styles.customColorGrid}>
            {CUSTOM_COLOR_OPTIONS.map((option) => {
              const active =
                themeKey === "custom" && customColor.key === option.key;
              return (
                <Pressable
                  key={option.key}
                  accessibilityRole="button"
                  accessibilityLabel={i18n.t("growth.share.useColorA11y", {
                    color: option.label,
                  })}
                  accessibilityState={{ selected: active }}
                  onPress={() => selectCustomColor(option)}
                  style={[
                    styles.customColorButton,
                    {
                      backgroundColor: option.background[0],
                      borderColor: active ? c.tint : c.border,
                      borderWidth: active ? 2 : 1,
                    },
                  ]}
                >
                  <View
                    style={[
                      styles.customColorDot,
                      { backgroundColor: option.accent },
                    ]}
                  />
                  {active ? (
                    <Ionicons
                      name="checkmark"
                      size={13}
                      color={option.accentDark}
                      style={styles.swatchIcon}
                    />
                  ) : null}
                </Pressable>
              );
            })}
          </View>
        </View>
      ) : null}

      {!isPlus ? (
        <ThemedText variant="caption" tone="muted" center>
          {t("shareProgress.morePosterStylesWithPlus")}
        </ThemedText>
      ) : null}

      <Modal
        visible={customizeOpen}
        transparent
        animationType="fade"
        onRequestClose={() => setCustomizeOpen(false)}
      >
        <View style={styles.customizeOverlay}>
          <BlurredBackdrop
            style={styles.customizeBackdrop}
            accessibilityLabel={i18n.t("growth.share.closeCustomizeA11y")}
            onPress={() => setCustomizeOpen(false)}
          />
          <View
            style={[
              styles.customizeSheet,
              { backgroundColor: c.surface, borderColor: c.border },
            ]}
          >
            <View style={styles.customizeHeader}>
              <View style={styles.customizeTitleWrap}>
                <ThemedText variant="title">
                  {i18n.t("growth.share.pickHighlights")}
                </ThemedText>
                <ThemedText variant="caption" tone="secondary">
                  {i18n.t("growth.share.pickHighlightsBody")}
                </ThemedText>
              </View>
              <Pressable
                accessibilityRole="button"
                accessibilityLabel={i18n.t("common.close")}
                onPress={() => setCustomizeOpen(false)}
                hitSlop={10}
              >
                <Ionicons name="close" size={23} color={c.textSecondary} />
              </Pressable>
            </View>

            <ScrollView
              style={styles.statOptionList}
              contentContainerStyle={styles.statOptionListContent}
              showsVerticalScrollIndicator={false}
            >
              {SHARE_STAT_GROUPS.map((group) => {
                const expanded = expandedStatGroup === group;
                const selectedCount = SHARE_STAT_OPTIONS.filter(
                  (option) =>
                    option.group === group &&
                    selectedStats.includes(option.key),
                ).length;
                return (
                  <View key={group} style={styles.statGroup}>
                    <Pressable
                      accessibilityRole="button"
                      accessibilityState={{ expanded }}
                      onPress={() =>
                        setExpandedStatGroup((current) =>
                          current === group ? null : group,
                        )
                      }
                      style={[
                        styles.statGroupHeader,
                        {
                          backgroundColor: c.surfaceAlt,
                          borderColor: expanded ? c.tint : c.border,
                        },
                      ]}
                    >
                      <View style={styles.statGroupHeaderCopy}>
                        <ThemedText variant="label" tone="muted">
                          {i18n.t(STAT_GROUP_KEYS[group]).toUpperCase()}
                        </ThemedText>
                        <ThemedText variant="caption" tone="secondary">
                          {i18n.t("growth.share.selectedCount", {
                            count: selectedCount,
                          })}
                        </ThemedText>
                      </View>
                      <Ionicons
                        name={expanded ? "chevron-up" : "chevron-down"}
                        size={18}
                        color={expanded ? c.tint : c.textMuted}
                      />
                    </Pressable>

                    {expanded
                      ? SHARE_STAT_OPTIONS.filter(
                          (option) => option.group === group,
                        ).map((option) => {
                    const selected = selectedStats.includes(option.key);
                    const count = option.value(summary);
                    const label = statLabel(option, summary);
                    return (
                      <View
                        key={option.key}
                        style={[
                          styles.statOption,
                          {
                            backgroundColor: selected
                              ? c.backgroundSelected
                              : c.surfaceAlt,
                            borderColor: selected ? c.tint : c.border,
                          },
                        ]}
                      >
                        <Pressable
                          accessibilityRole="checkbox"
                          accessibilityState={{ checked: selected }}
                          onPress={() => {
                            setSelectedStats((current) => {
                              // The card always carries three stats, so a
                              // pick never just leaves — it is replaced.
                              // Tapping a new option swaps out the oldest
                              // pick; tapping a chosen one keeps it.
                              if (current.includes(option.key)) return current;
                              if (current.length < 3) {
                                return [...current, option.key];
                              }
                              return [...current.slice(1), option.key];
                            });
                          }}
                          style={styles.statOptionMain}
                        >
                          <View
                            style={[
                              styles.statOptionIcon,
                              {
                                backgroundColor: selected
                                  ? c.tint
                                  : c.backgroundSelected,
                              },
                            ]}
                          >
                            <Ionicons
                              name={option.icon}
                              size={17}
                              color={selected ? c.onTint : c.tint}
                            />
                          </View>
                          <View style={styles.statOptionCopy}>
                            <ThemedText variant="bodyStrong">
                              {label}
                            </ThemedText>
                            <ThemedText variant="caption" tone="secondary">
                              {safeCount(count)}
                            </ThemedText>
                          </View>
                        </Pressable>
                      </View>
                    );
                        })
                      : null}
                  </View>
                );
              })}
            </ScrollView>

            <Pressable
              accessibilityRole="button"
              onPress={() => setCustomizeOpen(false)}
              style={({ pressed }) => [
                styles.customizeDone,
                { backgroundColor: c.tint },
                pressed && styles.pressed,
              ]}
            >
              <ThemedText variant="bodyStrong" tone="onTint">
                {i18n.t("common.done")}
              </ThemedText>
            </Pressable>
          </View>
        </View>
      </Modal>

      <Modal
        visible={previewOpen}
        animationType="slide"
        presentationStyle="fullScreen"
        onRequestClose={() => setPreviewOpen(false)}
      >
        <View style={[styles.fullScreen, { backgroundColor: c.background }]}>
          <View style={styles.fullHeader}>
            <ThemedText variant="title">
              {summary.rangeDays === 7
                ? t("shareProgress.yourProgressThisWeek")
                : t("shareProgress.yourProgressThisMonth")}
            </ThemedText>
            <Pressable
              accessibilityRole="button"
              accessibilityLabel={t("shareProgress.closePreviewA11y")}
              onPress={() => setPreviewOpen(false)}
              hitSlop={10}
            >
              <Ionicons name="close" size={25} color={c.textSecondary} />
            </Pressable>
          </View>

          {previewUri ? (
            <Image
              source={{ uri: previewUri }}
              resizeMode="contain"
              style={styles.fullPoster}
            />
          ) : (
            <View style={styles.fullPosterLoading}>
              <ActivityIndicator color={c.tint} />
            </View>
          )}

          <Pressable
            accessibilityRole="button"
            accessibilityLabel={t("shareProgress.shareProgress")}
            onPress={share}
            disabled={sharing}
            style={({ pressed }) => [
              styles.shareButton,
              { backgroundColor: c.tint },
              (pressed || sharing) && styles.pressed,
            ]}
          >
            <Ionicons
              name={sharing ? "image-outline" : "share-social-outline"}
              size={18}
              color={c.onTint}
            />
            <ThemedText variant="bodyStrong" tone="onTint">
              {sharing
                ? t("shareProgress.preparingImage")
                : t("shareProgress.shareProgress")}
            </ThemedText>
          </Pressable>
        </View>
      </Modal>
    </View>
  );
}

const styles = StyleSheet.create({
  wrap: {
    alignItems: "center",
    gap: Spacing.md,
  },
  exportHost: {
    width: 1,
    height: 1,
    overflow: "hidden",
  },
  exportFrame: {
    width: EXPORT_WIDTH,
    height: EXPORT_HEIGHT,
    backgroundColor: "#F4FAFF",
  },
  posterBackground: {
    flex: 1,
    overflow: "hidden",
    paddingHorizontal: 96,
    paddingVertical: 71,
    justifyContent: "center",
  },
  softShape: {
    position: "absolute",
    opacity: 0.62,
  },
  softShapeTop: {
    width: 680,
    height: 480,
    borderBottomLeftRadius: 480,
    right: -220,
    top: -210,
  },
  softShapeBottom: {
    width: 610,
    height: 420,
    borderTopLeftRadius: 420,
    right: -225,
    bottom: -150,
  },
  poster: {
    flex: 1,
    alignItems: "center",
    justifyContent: "space-between",
    borderRadius: 74,
    paddingHorizontal: 88,
    paddingTop: 59,
    paddingBottom: 46,
    // Tinted shadows can be captured as a solid accent fill by view-shot's web
    // html2canvas path, so the export uses an actual border for the card edge.
    shadowOpacity: 0,
    shadowRadius: 0,
    shadowOffset: { width: 0, height: 24 },
    elevation: 0,
    borderWidth: 2,
  },
  posterHeader: {
    alignItems: "center",
    gap: 17,
  },
  kicker: {
    fontFamily: Fonts.sans,
    fontSize: 30,
    fontWeight: "800",
    letterSpacing: 12,
    textAlign: "center",
    lineHeight: 36,
  },
  brand: {
    fontFamily: Fonts.sans,
    fontSize: 86,
    fontWeight: "800",
    letterSpacing: 0,
    lineHeight: 96,
  },
  dateStamp: {
    fontFamily: Fonts.sans,
    fontSize: 30,
    fontWeight: "800",
    letterSpacing: 0,
    lineHeight: 38,
  },
  headline: {
    fontFamily: Fonts.serif,
    fontSize: 67,
    fontWeight: "800",
    letterSpacing: 0,
    lineHeight: 78,
    textAlign: "center",
  },
  mainMetricWrap: {
    width: 410,
    height: 410,
    alignItems: "center",
    justifyContent: "center",
  },
  mainMetricHalo: {
    position: "absolute",
    width: 410,
    height: 410,
    borderRadius: 205,
    opacity: 0.92,
  },
  mainMetric: {
    width: 320,
    fontFamily: Fonts.serif,
    fontSize: 250,
    fontWeight: "800",
    letterSpacing: 0,
    lineHeight: 282,
    textAlign: "center",
  },
  mainMetricLabel: {
    fontFamily: Fonts.serif,
    fontSize: 54,
    fontWeight: "800",
    letterSpacing: 0,
    lineHeight: 62,
    textAlign: "center",
  },
  secondaryRow: {
    width: "100%",
    minHeight: 220,
    flexDirection: "row",
    // Stretch so both tiles adopt the tallest tile's height: if one label wraps
    // to two lines, the other mirrors it instead of sitting shorter.
    alignItems: "stretch",
    justifyContent: "center",
    gap: 32,
  },
  secondaryStatWrap: {
    flexDirection: "row",
    alignItems: "stretch",
    gap: 32,
  },
  secondaryRowSpacer: {
    minHeight: 220,
  },
  secondaryTile: {
    width: 316,
    minHeight: 212,
    borderRadius: 42,
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: 24,
    // Horizontal breathing room so longer labels (e.g. "Objectifs terminés")
    // don't run right up to the card edges.
    paddingHorizontal: 30,
  },
  secondaryValue: {
    width: 210,
    fontFamily: Fonts.sans,
    fontSize: 78,
    fontWeight: "800",
    letterSpacing: 0,
    lineHeight: 88,
    textAlign: "center",
  },
  secondaryLabel: {
    fontFamily: Fonts.serif,
    fontSize: 34,
    fontWeight: "800",
    letterSpacing: 0,
    lineHeight: 42,
    textAlign: "center",
  },
  secondaryDivider: {
    width: 3,
    height: 150,
    alignSelf: "center",
    opacity: 0.35,
  },
  posterFooter: {
    width: "100%",
    alignItems: "center",
  },
  footerRule: {
    width: "100%",
    flexDirection: "row",
    alignItems: "center",
    gap: 34,
    marginBottom: 31,
  },
  ruleLine: {
    flex: 1,
    height: 2,
    opacity: 0.3,
  },
  footer: {
    fontFamily: Fonts.serif,
    fontSize: 46,
    fontWeight: "800",
    letterSpacing: 0,
    lineHeight: 56,
    textAlign: "center",
  },
  safeStats: {
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
    marginTop: 38,
  },
  safeStatsText: {
    fontFamily: Fonts.sans,
    fontSize: 26,
    fontWeight: "700",
    letterSpacing: 0,
    lineHeight: 32,
  },
  previewButton: {
    width: "100%",
    alignItems: "center",
    marginTop: POSTER_GAP,
  },
  previewScrollFrame: {
    width: "100%",
    alignItems: "center",
    position: "relative",
  },
  previewScroll: {
    width: "100%",
  },
  previewScrollContent: {
    alignItems: "center",
  },
  previewScrollTrack: {
    position: "absolute",
    top: POSTER_GAP,
    right: 42,
    bottom: 0,
    width: 5,
    borderRadius: 999,
    borderWidth: 1,
    overflow: "hidden",
  },
  previewScrollThumb: {
    width: "100%",
    borderRadius: 999,
  },
  posterPreview: {
    width: "100%",
    maxWidth: 220,
    aspectRatio: Platform.OS === "web" ? EXPORT_WIDTH / EXPORT_HEIGHT : 0.64,
    alignSelf: "center",
    borderRadius: 22,
    overflow: "hidden",
  },
  previewLoading: {
    borderWidth: 1,
    alignItems: "center",
    justifyContent: "center",
    gap: Spacing.sm,
  },
  customizeButton: {
    minHeight: 43,
    maxWidth: 220,
    width: "100%",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: Spacing.sm,
    borderRadius: Radius.lg,
    borderWidth: 1,
    paddingHorizontal: Spacing.lg,
  },
  previewActionRow: {
    width: "100%",
    maxWidth: 220,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 10,
    marginTop: POSTER_GAP,
  },
  previewAction: {
    flex: 1,
    minHeight: 43,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: Spacing.sm,
    borderRadius: Radius.lg,
    borderWidth: 1,
    paddingHorizontal: Spacing.lg,
  },
  previewShareAction: {
    width: 43,
    height: 43,
    alignItems: "center",
    justifyContent: "center",
    borderRadius: Radius.lg,
    borderWidth: 1,
  },
  themeRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: Spacing.sm,
    justifyContent: "center",
  },
  swatch: {
    width: 32,
    height: 32,
    borderRadius: 16,
    alignItems: "center",
    justifyContent: "center",
  },
  swatchDot: {
    width: 18,
    height: 18,
    borderRadius: 9,
  },
  swatchIcon: {
    position: "absolute",
  },
  customColorPanel: {
    width: "100%",
    alignItems: "center",
    gap: Spacing.sm,
  },
  customColorGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "center",
    gap: Spacing.sm,
  },
  customColorButton: {
    width: 36,
    height: 36,
    borderRadius: 18,
    alignItems: "center",
    justifyContent: "center",
  },
  customColorDot: {
    width: 22,
    height: 22,
    borderRadius: 11,
  },
  fullScreen: {
    flex: 1,
    paddingTop: 58,
    paddingHorizontal: Spacing.lg,
    paddingBottom: 28,
  },
  customizeOverlay: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: Spacing.lg,
  },
  customizeBackdrop: {
    position: "absolute",
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    backgroundColor: "rgba(6, 18, 36, 0.48)",
  },
  customizeSheet: {
    width: "100%",
    maxWidth: 420,
    // Fixed height, NOT content-driven: expanding or collapsing a group must
    // never resize the sheet — the group list scrolls inside it instead. The
    // cap keeps it on-screen for short viewports.
    height: 560,
    maxHeight: "88%",
    gap: Spacing.md,
    borderRadius: Radius.xl,
    borderWidth: 1,
    padding: Spacing.lg,
  },
  customizeHeader: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: Spacing.md,
  },
  customizeTitleWrap: {
    flex: 1,
    gap: Spacing.xs,
  },
  statOptionList: {
    // Fills the space between the header and the pinned Done button, so the
    // sheet's size never follows the list's content.
    flex: 1,
  },
  statOptionListContent: {
    gap: Spacing.sm,
  },
  statGroup: { gap: Spacing.xs },
  statGroupHeader: {
    minHeight: 58,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: Spacing.md,
    paddingHorizontal: Spacing.md,
    paddingVertical: Spacing.sm,
    borderRadius: Radius.lg,
    borderWidth: 1,
  },
  statGroupHeaderCopy: {
    flex: 1,
    minWidth: 0,
    gap: 2,
  },
  statOption: {
    minHeight: 58,
    flexDirection: "row",
    alignItems: "center",
    gap: Spacing.sm,
    borderRadius: Radius.lg,
    borderWidth: 1,
    paddingHorizontal: Spacing.md,
    paddingVertical: Spacing.sm,
  },
  statOptionMain: {
    flex: 1,
    minWidth: 0,
    flexDirection: "row",
    alignItems: "center",
    gap: Spacing.sm,
  },
  statOptionIcon: {
    width: 34,
    height: 34,
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 17,
  },
  statOptionCopy: {
    flex: 1,
    minWidth: 0,
  },
  customizeDone: {
    minHeight: 46,
    alignItems: "center",
    justifyContent: "center",
    borderRadius: Radius.lg,
  },
  fullHeader: {
    minHeight: 44,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: Spacing.md,
    marginBottom: Spacing.md,
  },
  fullPoster: {
    width: "100%",
    height: "76%",
    maxWidth: 420,
    alignSelf: "center",
  },
  fullPosterLoading: {
    width: "100%",
    height: "76%",
    alignItems: "center",
    justifyContent: "center",
  },
  shareButton: {
    width: "100%",
    minHeight: 52,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: Spacing.sm,
    borderRadius: Radius.lg,
    marginTop: 24,
  },
  pressed: { opacity: 0.82 },
});
